import { useRef, useState } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ImagePlus, Upload } from 'lucide-react';
import { Sheet, SheetBody, SheetContent } from './ui/Sheet';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Alert } from './ui/Alert';
import { apiFetch } from '../lib/api';
import { ApiError } from '../types/api';
import type { MediaImage, MediaListResponse, MediaUploadResponse } from '../types/api';

/** Same limit as the server (`MEDIA_MAX_BYTES`); checked here only to save the upload. */
const MAX_BYTES = 2 * 1024 * 1024;
const ACCEPT = 'image/jpeg,image/png,image/gif,image/webp';
const PAGE_SIZE = 24;

const TOO_LARGE = 'The image is larger than 2 MB. Make it smaller and try again.';

function uploadErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 415) return 'Only JPEG, PNG, GIF and WebP images can be used.';
    if (err.status === 413) return TOO_LARGE;
    if (err.status === 403) return 'Your role cannot upload images. Ask an editor or an admin.';
    if (err.status === 409 && err.body.message) return err.body.message;
    if (err.status === 503) return 'Image upload is not set up for this environment yet.';
    if (err.status === 400 && err.body.error === 'empty') return 'The file is empty.';
  }
  return 'The upload failed. Try again.';
}

function describeSize(image: MediaImage): string {
  const kb = Math.max(1, Math.round(image.byteSize / 1024));
  const dims = image.width > 0 && image.height > 0 ? `${image.width} × ${image.height} · ` : '';
  return `${dims}${kb} KB`;
}

function MediaPickerBody({ onPick }: { onPick: (url: string) => void }) {
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const list = useInfiniteQuery({
    queryKey: ['media'],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      apiFetch<MediaListResponse>(
        `/media?limit=${PAGE_SIZE}${pageParam ? `&before=${encodeURIComponent(pageParam)}` : ''}`,
      ),
    getNextPageParam: (last) => last.nextBefore,
  });

  const upload = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.set('file', file);
      return apiFetch<MediaUploadResponse>('/media', { method: 'POST', body: form });
    },
    onSuccess: ({ image }) => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['media'] });
      onPick(image.url);
    },
    onError: (err) => setError(uploadErrorText(err)),
  });

  function start(file: File | undefined) {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setError(TOO_LARGE);
      return;
    }
    setError(null);
    upload.mutate(file);
  }

  const images = list.data?.pages.flatMap((p) => p.images) ?? [];

  return (
    <SheetBody>
      <div
        className={
          'flex flex-col items-center gap-2 rounded-xl border border-dashed px-4 py-6 text-center ' +
          (dragging ? 'border-accent bg-bg' : 'border-border')
        }
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          start(e.dataTransfer.files[0]);
        }}
      >
        <Upload size={20} aria-hidden="true" className="text-muted" />
        <Button
          variant="primary"
          size="sm"
          disabled={upload.isPending}
          onClick={() => fileInput.current?.click()}
        >
          {upload.isPending ? 'Uploading…' : 'Upload from computer'}
        </Button>
        <input
          ref={fileInput}
          type="file"
          accept={ACCEPT}
          aria-label="Upload an image"
          tabIndex={-1}
          className="sr-only"
          onChange={(e) => {
            start(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
        <p className="text-xs text-muted m-0">
          or drop a file here. JPEG, PNG, GIF or WebP, up to 2 MB. The image is used as it is, so
          size it for email first (600 to 1200 px wide is plenty).
        </p>
      </div>

      {error ? <Alert>{error}</Alert> : null}

      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold m-0">Earlier uploads</h3>
        {list.isPending ? <p className="text-sm text-muted m-0">Loading…</p> : null}
        {list.isError ? <Alert>Could not load earlier uploads.</Alert> : null}
        {list.isSuccess && images.length === 0 ? (
          <p className="text-sm text-muted m-0">Nothing uploaded yet.</p>
        ) : null}
        <ul className="grid grid-cols-2 gap-3 list-none m-0 p-0">
          {images.map((image) => (
            <li key={image.id}>
              <button
                type="button"
                aria-label={`Use ${image.name ?? 'image'}`}
                className="w-full text-left rounded-xl border border-border bg-white p-2 hover:border-accent focus-visible:outline-2 focus-visible:outline-accent"
                onClick={() => onPick(image.url)}
              >
                <img
                  src={image.url}
                  alt=""
                  loading="lazy"
                  className="w-full aspect-[4/3] object-contain rounded-md bg-bg"
                />
                <span className="block mt-1.5 text-xs font-medium truncate">
                  {image.name ?? 'Image'}
                </span>
                <span className="block text-xs text-muted">{describeSize(image)}</span>
              </button>
            </li>
          ))}
        </ul>
        {list.hasNextPage ? (
          <Button
            variant="secondary"
            size="sm"
            disabled={list.isFetchingNextPage}
            onClick={() => void list.fetchNextPage()}
          >
            Show older
          </Button>
        ) : null}
      </div>
    </SheetBody>
  );
}

/**
 * The image field of the image and product blocks: the URL box as before (typing or
 * pasting a URL still works) plus a button that opens a right-hand panel to upload a file
 * or pick an earlier upload. Either way the result is just a URL in the block, so the
 * preview, preflight and sending work the same for typed and uploaded images.
 *
 * `variant` only picks the markup of the text box: `card` is the stacked editor
 * (aria-label), `row` is the collapsible row editor (the caller's `<label for={id}>`).
 */
export function ImageSourceInput({
  id,
  label,
  value,
  onChange,
  variant,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  variant: 'card' | 'row';
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex items-center gap-2">
      <div className="flex-grow min-w-0">
        {variant === 'card' ? (
          <Input
            id={id}
            aria-label={label}
            placeholder="Image URL"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        ) : (
          <input id={id} type="text" value={value} onChange={(e) => onChange(e.target.value)} />
        )}
      </div>
      <Button
        variant="secondary"
        size="sm"
        className="flex-shrink-0"
        aria-label="Upload or choose image"
        onClick={() => setOpen(true)}
      >
        <ImagePlus size={15} aria-hidden="true" />
        Upload
      </Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          eyebrow="Images"
          title="Choose an image"
          description="Upload a file from your computer or reuse an image you uploaded before."
        >
          <MediaPickerBody
            onPick={(url) => {
              onChange(url);
              setOpen(false);
            }}
          />
        </SheetContent>
      </Sheet>
    </div>
  );
}
